import type { Site } from '../../schemas/validation';

export const getThemeKeyForComponentType = (type: string): string | null => {
  const themeKeyMap: Record<string, string> = {
    navbar: 'navbar',
    'navigation-menu': 'navigationMenu',
    hero: 'hero',
    'split-hero': 'splitHero',
    'simple-header': 'simpleHeader',
    features: 'features',
    cta: 'cta',
    footer: 'footer',
    form: 'form',
    'form-split': 'form',
    pricing: 'pricing',
    team: 'team',
    'media-text': 'mediaText',
    'text-blocks': 'textBlocks',
    'title-subtitle': 'titleSubtitle',
    'contact-info': 'contactInfo',
    video: 'video',
    faq: 'faq',
    testimonials: 'testimonials',
    'countdown-timer': 'countdownTimer',
    'image-gallery': 'imageGallery',
    'image-info': 'imageInfo',
    'image-card': 'imageCard',
  };
  return themeKeyMap[type] || null;
};

export const getDefaultDataForType = (type: string, site: Site | null, _themeColors: any): any => {
  // NOTE: We intentionally do NOT bake brand-package / theme colours into the
  // component defaults.  Colours are resolved at render time from the active
  // brand package (via CustomColorsContext on the frontend and mergeThemeIntoData
  // on the backend).  Baking them in here would cause layouts to keep stale
  // colours after a brand package change.

  switch (type) {
    case 'navbar':
      return {
        siteName: site?.name || 'Site Name',
        links: [
          {
            id: `link-${Date.now()}-0`,
            label: 'Home',
            text: 'Home',
            url: '/',
            target: '_self',
            order: 0,
            style: 'normal',
          },
          {
            id: `link-${Date.now()}-1`,
            label: 'About',
            text: 'About',
            url: '/about',
            target: '_self',
            order: 1,
            style: 'normal',
          },
          {
            id: `link-${Date.now()}-2`,
            label: 'Contact',
            text: 'Contact',
            url: '/contact',
            target: '_self',
            order: 2,
            style: 'button',
          },
        ],
        logoUrl: '',
        showLogo: false,
        sticky: true,
        alignment: 'space-between',
        height: 'normal',
        shadow: true,
        borderBottom: false,
      };
    case 'navigation-menu':
      return {
        siteName: site?.name || 'Site Name',
        links: [
          {
            id: `link-${Date.now()}-0`,
            text: 'Home',
            url: '/',
            target: '_self',
            order: 0,
            style: 'normal',
          },
        ],
        sticky: true,
        alignment: 'space-between',
        height: 'normal',
        shadow: true,
        borderBottom: false,
      };
    case 'footer':
      return {
        siteName: site?.name || 'Your Company',
        description: 'We are dedicated to providing exceptional services.',
        copyright: `© ${new Date().getFullYear()} ${site?.name || 'Your Company'}. All rights reserved.`,
        links: [
          {
            id: `link-${Date.now()}-0`,
            text: 'About Us',
            url: '/about',
            target: '_self',
            category: 'Company',
            order: 0,
          },
          {
            id: `link-${Date.now()}-1`,
            text: 'Contact',
            url: '/contact',
            target: '_self',
            category: 'Company',
            order: 1,
          },
        ],
        socialLinks: [],
      };
    case 'content-area':
      return {
        label: 'Page Content',
        minHeight: 200,
        backgroundColor: 'transparent',
        maxWidth: 'container',
        centered: true,
        padding: { top: 0, bottom: 0, left: 0, right: 0 },
      };
    case 'hero':
      return {
        tagline: 'Welcome to Excellence',
        title: 'We Help Your Business Grow and Succeed',
        subtitle: 'Transform your vision into reality with our innovative solutions.',
        buttons: [
          {
            id: `btn-${Date.now()}-0`,
            text: 'Get Started',
            url: '/contact',
            style: 'primary',
            target: '_self',
            order: 0,
          },
          {
            id: `btn-${Date.now()}-1`,
            text: 'Learn More',
            url: '/about',
            style: 'outline',
            target: '_self',
            order: 1,
          },
        ],
        alignment: 'center',
        size: 'large',
      };
    case 'features':
      return {
        title: 'Why Choose Us',
        subtitle: 'Discover the advantages that set us apart',
        features: [
          {
            id: `feat-${Date.now()}-0`,
            title: 'Expert Team',
            description: 'Experienced professionals at your service.',
            icon: 'users',
            order: 0,
          },
          {
            id: `feat-${Date.now()}-1`,
            title: 'Quality Service',
            description: 'High-quality solutions that exceed expectations.',
            icon: 'award',
            order: 1,
          },
          {
            id: `feat-${Date.now()}-2`,
            title: '24/7 Support',
            description: 'Round-the-clock customer support.',
            icon: 'support',
            order: 2,
          },
        ],
        layout: 'grid',
        columns: '3',
        displayMode: 'icon',
        showIcons: true,
      };
    case 'cta':
      return {
        title: 'Ready to Get Started?',
        subtitle: 'Join thousands of satisfied customers today.',
        buttons: [
          {
            id: `btn-${Date.now()}-0`,
            text: 'Start Free Trial',
            url: '/signup',
            style: 'primary',
            target: '_self',
            order: 0,
          },
        ],
        alignment: 'center',
        size: 'medium',
      };
    case 'team':
      return {
        title: 'Meet Our Team',
        subtitle: 'The passionate people behind our success',
        members: [],
        layout: 'grid',
        columns: '3',
      };
    case 'testimonials':
      return {
        title: 'What Our Clients Say',
        subtitle: 'Hear from our satisfied customers.',
        items: [],
        layout: 'grid',
        showRating: true,
        showAvatar: true,
      };
    case 'faq':
      return {
        title: 'Frequently Asked Questions',
        subtitle: 'Find answers to common questions.',
        items: [],
        layout: 'accordion',
        allowMultipleOpen: false,
      };
    case 'contact-info':
      return {
        title: 'Contact Us',
        subtitle: 'Get in Touch',
        businessName: site?.name || 'Your Business Name',
        address: '123 Main Street, City, State 12345',
        phone: '(555) 123-4567',
        email: 'contact@yourbusiness.com',
      };
    case 'countdown-timer': {
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + 7);
      return {
        headline: 'Limited Time Offer',
        subheadline: "Don't miss out — act before time runs out!",
        showHeadline: true,
        showSubheadline: true,
        headlineAlignment: 'center',
        headlineSize: 'medium',
        targetDate: targetDate.toISOString(),
        displayFormat: 'full',
        layout: 'horizontal',
        alignment: 'center',
        size: 'medium',
        units: {
          showDays: true,
          showHours: true,
          showMinutes: true,
          showSeconds: true,
        },
        numberStyle: 'default',
        separatorStyle: 'colon',
        showLabels: true,
        expiredMessage: 'This offer has ended',
        hideWhenExpired: false,
        buttons: [],
      };
    }
    default:
      return {};
  }
};
